import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowDown } = FiIcons;

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToNext = () => {
    const nextSection = document.getElementById('problem');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-booking');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-accent-light/30 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="text-accent-magenta font-semibold text-lg tracking-wide uppercase font-sans"
              >
                Don't Wait. Build Their Foundation.
              </motion.p>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight font-serif"
              >
                Their Future{' '}
                <span className="gradient-text">Starts Here</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                className="text-lg text-gray-700 leading-relaxed max-w-xl"
              >
                Worried your child isn't hitting milestones? Their developing brain needs the right foundation now. We help build those critical early skills with specialized communication, feeding, and cognitive therapy that grows with them, not just manages delays.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="text-base font-semibold text-gray-900 max-w-xl"
              >
                At this exact moment, their brain is at its most adaptable. With the right help, we can build strong foundations for talking, eating, learning, and thriving.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6 }}
                className="text-lg font-bold text-accent-magenta max-w-xl"
              >
                We build foundations that last a lifetime. Starting now.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToContact}
                className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold hover:bg-accent-magenta/90 transition-all duration-200 shadow-lg"
              >
                Book Your Child's Future Call
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToNext}
                className="border-2 border-accent-magenta text-accent-magenta px-6 py-3 rounded-full font-semibold hover:bg-accent-magenta hover:text-white transition-all duration-200 group flex items-center justify-center"
              >
                <motion.div
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <SafeIcon icon={FiArrowDown} className="h-5 w-5" />
                </motion.div>
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative flex justify-center"
          >
            <div className="relative z-10 max-w-lg">
              <img
                src="https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=600&h=600&fit=crop&crop=face"
                alt="Happy child laughing while playing with colorful building blocks during early intervention therapy"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-accent-gold/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-accent-magenta/20 rounded-full blur-2xl"></div>
            <div className="absolute top-1/2 -right-16 w-20 h-20 bg-accent-lavender/30 rounded-full blur-xl"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;